import { Box, IconButton, Typography, Chip, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EuroIcon from '@mui/icons-material/EuroSymbol';
import { useTranslation } from 'react-i18next';
import type { TimeEntry } from '@altyor-kronos/shared-types';
import { formatDurationSeconds, formatEntryDateTime } from './timeEntries';
import { colors } from '../../core/theme/theme';
import { entryGridTemplate } from './entryGrid';

export interface TimeEntryRowProps {
  entry: TimeEntry;
  /** Resolved project display label — code + name, supplied by the parent. */
  projectLabel?: string;
  /** Resolved designation display label. */
  designationLabel?: string;
  /** Resolved teammate display name (only used when `showUser` is true). */
  userLabel?: string;
  /**
   * When `true`, renders the teammate column. Parent computes this via
   * `shouldShowUserColumn(user.roles)` — UI-level parity with rules.
   */
  showUser?: boolean;
  /**
   * When `false`, the Project column is skipped. Used by the per-
   * project Kronos tab where every row is on the same project.
   * Defaults to `true` for the tracker "Today" list which spans
   * multiple projects.
   */
  showProject?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  /**
   * When supplied, clicking anywhere on the row (outside the edit
   * and delete icon buttons) invokes this callback — typically to
   * open a detail drawer.
   */
  onClick?: () => void;
}

/**
 * A single row in the Kronos entry list. Lays out: duration · project ·
 * designation · description · tags · billable — plus the teammate
 * column when shown. Edit/delete controls render only when the parent
 * wires `onEdit`/`onDelete` (the per-project tab is read-only and omits
 * them).
 */
export default function TimeEntryRow({
  entry,
  projectLabel,
  designationLabel,
  userLabel,
  showUser,
  showProject = true,
  onEdit,
  onDelete,
  onClick,
}: TimeEntryRowProps) {
  const { t, i18n } = useTranslation();
  const startDateLabel = formatEntryDateTime(entry.startTime.toDate(), i18n.language);
  return (
    <Box
      data-testid={`time-entry-row-${entry.id}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          md: entryGridTemplate({ showProject, showUser: !!showUser }),
        },
        alignItems: 'center',
        gap: 2,
        px: 2,
        py: 1.25,
        borderTop: `1px solid ${colors.lightGray}`,
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': onClick ? { backgroundColor: colors.lightGray } : undefined,
      }}
    >
      <Typography
        variant="body2"
        sx={{ fontVariantNumeric: 'tabular-nums' }}
        data-testid={`time-entry-when-${entry.id}`}
      >
        {startDateLabel}
      </Typography>

      <Typography
        variant="body2"
        sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
        data-testid={`time-entry-duration-${entry.id}`}
      >
        {formatDurationSeconds(entry.durationSeconds)}
      </Typography>

      {showProject && (
        <Typography variant="body2" noWrap title={projectLabel}>
          {projectLabel ?? entry.projectId}
        </Typography>
      )}

      <Typography variant="body2" noWrap title={designationLabel}>
        {designationLabel ?? entry.designationId}
      </Typography>

      {showUser && (
        <Typography
          variant="body2"
          noWrap
          color="text.secondary"
          data-testid={`time-entry-user-${entry.id}`}
        >
          {userLabel ?? entry.userId}
        </Typography>
      )}

      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" color="text.secondary" noWrap>
          {entry.description ?? ''}
        </Typography>
        {(entry.tags?.length ?? 0) > 0 && (
          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
            {entry.tags.map((tag) => (
              <Chip key={tag} label={tag} size="small" variant="outlined" />
            ))}
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', justifyContent: 'flex-end' }}>
        {entry.billable && (
          <Tooltip title={t('kronos.entries.billableTooltip')}>
            <Box
              sx={{ display: 'flex', alignItems: 'center', color: 'primary.main' }}
              data-testid={`time-entry-billable-${entry.id}`}
            >
              <EuroIcon sx={{ fontSize: 16 }} aria-label={t('kronos.entries.billableTooltip')} />
            </Box>
          </Tooltip>
        )}
        {onEdit && (
          <IconButton
            size="small"
            aria-label={t('kronos.entries.edit')}
            onClick={(e) => {
              // Don't also trigger the row's onClick (detail drawer).
              e.stopPropagation();
              onEdit();
            }}
            data-testid={`time-entry-edit-${entry.id}`}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        )}
        {onDelete && (
          <IconButton
            size="small"
            aria-label={t('kronos.entries.delete')}
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            data-testid={`time-entry-delete-${entry.id}`}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    </Box>
  );
}
